/**
 * 主題圖鑑（介紹型，非購買）mock — DARTSLIVE / Fun Dart / Award Movie 歷年主題收錄。
 * 對應 API: GET /api/themes、GET /api/themes/:id
 *
 * 後端完成後，用 REACT_APP_USE_MOCK_THEMES=false 切真後端，此檔可移除。
 */

// 主題類型（唯一分類，四種）
export const THEME_TYPES = {
  d2:    { label: 'DARTSLIVE 2', short: 'D2' },
  d3:    { label: 'DARTSLIVE 3', short: 'D3' },
  fun:   { label: 'Fun Dart',    short: 'FUN' },
  award: { label: 'Award Movie', short: 'AWARD' },
};

// 沒有上傳圖片時的卡片預設底色（依類型）
export const THEME_FALLBACK_BG = {
  d2:    'linear-gradient(135deg, #E5002B, #7a0018)',
  d3:    'linear-gradient(135deg, #001A3D, #002F6E 50%, #00B8FF)',
  fun:   'linear-gradient(135deg, #F5B815, #C8941E)',
  award: 'linear-gradient(135deg, #1E0E5C, #6B1FAE 50%, #FF3CE6)',
};

// 取得狀態（後台選一種）：常駐 / 期間限定 / 已絕版。
// 期間限定會搭配起訖日期，由 resolveThemeStatus 依今天自動推算實際狀態。
export const THEME_AVAILABILITY = {
  permanent: { label: '常駐可取得', badge: '常駐' },
  limited:   { label: '期間限定',   badge: '限定中' },
  ended:     { label: '已絕版',     badge: '已絕版' },
};

// 說明文字留空時的預設值（後台沒填就用這個）
export const DEFAULT_AVAILABLE_NOTE = '可向持有可複製版本的玩家進行複製。';
export const DEFAULT_ENDED_NOTE = '目前無法再透過任何方式取得。';

// 本地今天的 YYYY-MM-DD。日期都是 ISO 字串，直接字串比大小即可，避開時區誤差。
function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * 解析主題目前的取得狀態。後台存的是 availability（permanent/limited/ended）；
 * 期間限定會依今天的日期自動推算：未到開始日 → upcoming（即將開放）、
 * 過了結束日 → 自動視為 ended（絕版），期間內 → available（限定中）。
 *
 * 回傳 { state, isEnded, badge, limited, expired, from, until }
 *   state: 'available' | 'upcoming' | 'ended'
 * 舊資料只有 ended 布林時自動轉換，保持相容。
 */
export function resolveThemeStatus(t = {}) {
  const av = t.availability || (t.ended ? 'ended' : 'permanent');

  if (av === 'ended') {
    return { state: 'ended', isEnded: true, badge: '已絕版' };
  }

  if (av === 'limited') {
    const from = t.availableFrom || '';
    const until = t.availableUntil || '';
    const today = todayISO();
    if (from && today < from) {
      return { state: 'upcoming', isEnded: false, badge: '即將開放', limited: true, from, until };
    }
    if (until && today > until) {
      return { state: 'ended', isEnded: true, badge: '已絕版', limited: true, expired: true, from, until };
    }
    return { state: 'available', isEnded: false, badge: '限定中', limited: true, from, until };
  }

  return { state: 'available', isEnded: false, badge: '常駐' };
}

export const THEMES = [
  { id: 100001, name: '極光 AURORA', type: 'd3', source: 'COIN STORE', availability: 'ended',
    images: [], videoUrl: '',
    story: '2021 年冬季推出的動畫主題，以北歐極光為靈感。命中三鏢時背景會綻放綠紫交織的光帶。當年是 DARTSLIVE 3 上市初期最受歡迎的主題之一，於 2023 年下架。' },
  { id: 100002, name: '霓虹東京 NEON TOKYO', type: 'd3', source: 'COIN STORE', availability: 'ended',
    images: [], videoUrl: '',
    story: '以新宿歌舞伎町夜景為主題的動畫背景，搭配電子合成音效。進站動畫會有霓虹招牌依序亮起的效果。為 2022 夏季「都市系列」的代表作。' },
  { id: 100003, name: '網絡駭客 CYBER', type: 'd3', source: 'MILE STORE', availability: 'ended',
    images: [], videoUrl: '',
    story: 'DARTSLIVE 3 限定特效，達成 180（TON80）時整個畫面會出現數位矩陣崩解特效。需以 Mile（里程點數）兌換，是進階玩家的身份象徵。' },
  { id: 100004, name: 'TON80 達成動畫', type: 'award', source: 'COIN STORE', availability: 'ended',
    images: [], videoUrl: '',
    story: '達成單回合 180 分時播放的 AWARD MOVIE。烈焰從鏢靶中心噴發，搭配觀眾歡呼音效。是飛鏢玩家最想收集的成就動畫之一。' },
  // 期間限定 × 已過期：示範到期自動絕版 + 自訂絕版說明
  { id: 100005, name: '錦鯉 KOI', type: 'd2', source: '期間限定', availability: 'limited',
    availableFrom: '2022-06-01', availableUntil: '2022-06-10',
    endedNote: '端午限定期間已結束，此主題不再上架，目前無法以任何方式取得。',
    images: [], videoUrl: '',
    story: '2022 年端午期間限定主題，紅金錦鯉躍出水面。命中時水波擴散，搭配和太鼓音效。限定期間結束後即絕版，目前無法取得。' },
  // 期間限定 × 進行中：示範限定中 + 可取得至 X，搭配自訂說明
  { id: 100006, name: '櫻花季 SAKURA', type: 'd2', source: '期間限定', availability: 'limited',
    availableFrom: '2026-05-01', availableUntil: '2026-06-30',
    availableNote: '春季限定上架中，於機台 D2 主題商店即可套用，期間結束後下架。',
    images: [], videoUrl: '',
    story: 'DARTSLIVE 2 春季限定圖片主題，滿開的櫻花並蒂。每年 3-4 月限時上架，是最早一批季節主題。' },
  // 常駐 × 自訂可取得說明
  { id: 100007, name: '大富翁模式 MONOPOLY', type: 'fun', source: '免費', availability: 'permanent',
    availableNote: '機台常駐玩法，免費遊玩，無需兌換或購買。',
    images: [], videoUrl: '',
    story: 'Fun Dart 派對玩法之一。把飛鏢盤變成大富翁棋盤，擲中的數字決定前進格數，適合多人同樂。為機台常駐免費玩法。' },
  // 常駐 × 用預設文字（availableNote 留空）
  { id: 100008, name: '射氣球 BALLOON POP', type: 'fun', source: '免費', availability: 'permanent',
    images: [], videoUrl: '',
    story: 'Fun Dart 休閒玩法，畫面浮現彩色氣球，擲中即爆破計分。新手暖身、小朋友也能玩的入門模式。常駐免費。' },
];
